import React, {useCallback, useContext, useEffect, useMemo, useRef, useState,} from 'react'
import {useIntl} from 'react-intl'
import {NavContext} from '../../context/global_context'
import {useNavigate, useParams, useSearchParams} from 'react-router-dom'
import {deleteRequest, getRequest, postRequest} from '../../services/base'
import {buildPageUrl, downloadErrorLogs, getDeleteTargetPage} from '../../utils/common'
import {downloadAsZip} from '../../components/connector_views/utils'
import {notifyError, notifySuccess} from '../../utils/notifications'
import useDebouncedValue from '../../utils/common_function'
import JsonTreeSearch from './JsonTreeSearch'
import LinkWithStatus from '../common/LinkWithStatus'
import Button from '../common/Button'
import InputText from '../common/InputText'
import SelectInput from '../common/SelectInput'
import {Dropdown, Empty, Flex, Form, Image, Modal, Row, Space, Table, Typography,} from 'antd'
import {
    ApartmentOutlined,
    DeleteOutlined,
    DownloadOutlined,
    EditOutlined,
    ExclamationCircleOutlined,
    FileTextOutlined,
    MoreOutlined,
    ThunderboltOutlined,
    UploadOutlined,
} from '@ant-design/icons'
import RecordsCount from '../common/RecordsCount'

const PAGE_SIZE = 10

const ConnectorViewsTable = () => {
  let { vehicle } = useParams()
  const intl = useIntl()
  const navigate = useNavigate()
  const [value, dispatch] = useContext(NavContext)
  const [searchParams, setSearchParams] = useSearchParams()
  const [modal, contextHolder] = Modal.useModal()
  const [form] = Form.useForm()

  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '')
  const [harnessSearchQuery, setHarnessSearchQuery] = useState(searchParams.get('hq') || '')
  const [expandedRowKeys, setExpandedRowKeys] = useState([])
  const [loading, setLoading] = useState(false)
  const [connectorViews, setConnectorViews] = useState([])
  const [pagination, setPagination] = useState({
    totalCount: 0,
    nextPageLink: null,
    prevPageLink: null,
    currentPage: 1,
    totalPages: 0,
  })
  const [rowJsonMap, setRowJsonMap] = useState({})
  const searchQueryRef = useRef(searchParams.get('q') || '')
  const harnessSearchQueryRef = useRef(searchParams.get('hq') || '')
  const currentUrl = useRef(
    `/api/connector_views/?vehicle=${vehicle}&search=${
      searchParams.get('q') || ''
    }&harness_search=${searchParams.get('hq') || ''}&limit=${PAGE_SIZE}`
  )
  const fileInputRef = useRef(null)
  const uploadRowIdRef = useRef(null)

  const debouncedSearch = useDebouncedValue(searchQuery, 500)
  const debouncedHarnessSearch = useDebouncedValue(harnessSearchQuery, 500)

  useEffect(() => {
    form.setFieldsValue({ search: searchQuery, harness_search: harnessSearchQuery })
  }, [searchQuery, harnessSearchQuery, form])

  // Auto-search on debounced name search change (skip initial render)
  const isFirstRender = useRef(true)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      getConnectorViewsData()
      return
    }
    updateUrlAndFetch(debouncedSearch, debouncedHarnessSearch)
  }, [debouncedSearch, debouncedHarnessSearch])

  const updateUrlAndFetch = (search, harnessSearch) => {
    const params = {}
    if (search) params.q = search
    if (harnessSearch) params.hq = harnessSearch
    setSearchParams(params, { replace: true })
    searchQueryRef.current = search
    harnessSearchQueryRef.current = harnessSearch
    currentUrl.current = `/api/connector_views/?vehicle=${vehicle}&search=${
      search || ''
    }&harness_search=${harnessSearch || ''}&limit=${PAGE_SIZE}`
    getConnectorViewsData()
  }

  const getConnectorViewsData = async (url) => {
    setLoading(true)
    try {
      const requestUrl = url || currentUrl.current
      let response = await getRequest(requestUrl)
      // base.js no longer rejects on a failed request, so the catch below only
      // sees genuine programming errors - the failure branch has to be explicit.
      if (response?.status === false) {
        notifyError(
          dispatch,
          intl.formatMessage({ id: 'connectorViews.list.loadFailed' })
        )
        setConnectorViews([])
      } else if (response?.results?.length > 0) {
        setConnectorViews(response.results)
        setPagination(response.pagination)
      } else {
        setConnectorViews([])
      }
    } catch (error) {
      notifyError(
        dispatch,
        error?.message ||
          intl.formatMessage({ id: 'connectorViews.list.loadFailed' })
      )
    } finally {
      setLoading(false)
    }
  }

  const confirmDelete = (id, name) => {
    modal.confirm({
      title: intl.formatMessage({
        id: 'connectorViews.list.deleteConfirmTitle',
      }),
      icon: <ExclamationCircleOutlined />,
      content: intl.formatMessage(
        { id: 'connectorViews.list.deleteConfirmContent' },
        { name }
      ),
      okText: intl.formatMessage({ id: 'connectorViews.list.delete' }),
      okType: 'danger',
      centered: true,
      maskClosable: true,
      onOk: () => deleteInstance(id),
    })
  }

  const deleteInstance = async (id) => {
    try {
      const res = await deleteRequest(
        `/api/connector_views/delete_views/${id}/instance/`
      )
      // base.js no longer rejects on a failed request, so the catch below only
      // sees genuine programming errors. Without this branch a failed delete
      // fell through and reported success.
      if (res?.status === false) {
        notifyError(
          dispatch,
          intl.formatMessage({ id: 'connectorViews.list.deleteFailed' })
        )
        return
      }
      notifySuccess(
        dispatch,
        intl.formatMessage({ id: 'connectorViews.list.deleteSuccess' })
      )
      currentUrl.current = buildPageUrl(currentUrl.current, getDeleteTargetPage(pagination))
      getConnectorViewsData()
    } catch (error) {
      notifyError(
        dispatch,
        error?.message ||
          intl.formatMessage({ id: 'connectorViews.list.deleteFailed' })
      )
    }
  }

  const confirmValidate = (id) => {
    modal.confirm({
      title: intl.formatMessage({ id: 'connectorViews.list.validateTitle' }),
      icon: <ExclamationCircleOutlined />,
      content: intl.formatMessage({
        id: 'connectorViews.list.validateConfirmContent',
      }),
      centered: true,
      maskClosable: true,
      onOk: () => validateUpload(id),
    })
  }

  const validateUpload = async (id) => {
    try {
      let res = await postRequest(`/api/connector_views/validate-upload/${id}/`)
      if (res?.data?.length > 0) {
        downloadErrorLogs(res?.data, 'svg-missing-references.txt')
      }
      if (res?.status) {
        notifySuccess(
          dispatch,
          intl.formatMessage({ id: 'connectorViews.list.validateSuccess' })
        )
      } else {
        notifyError(
          dispatch,
          intl.formatMessage({ id: 'connectorViews.list.validateFailed' })
        )
      }
    } catch (error) {
      notifyError(
        dispatch,
        error?.message ||
          intl.formatMessage({ id: 'connectorViews.list.validateFailed' })
      )
    }
  }

  const confirmAutoMap = (id) => {
    modal.confirm({
      title: intl.formatMessage({ id: 'connectorViews.list.autoMapTitle' }),
      icon: <ExclamationCircleOutlined />,
      content: intl.formatMessage({
        id: 'connectorViews.list.autoMapConfirmContent',
      }),
      centered: true,
      maskClosable: true,
      onOk: () => startAutoMapping(id),
    })
  }

  const startAutoMapping = async (id) => {
    try {
      const formData = new FormData()
      let res = await postRequest(
        `/api/connector_views/auto-mapping/${id}/`,
        formData
      )
      if (res?.status) {
        notifySuccess(
          dispatch,
          intl.formatMessage({ id: 'connectorViews.list.autoMapSuccess' })
        )
      } else {
        notifyError(
          dispatch,
          intl.formatMessage({ id: 'connectorViews.list.autoMapFailed' })
        )
      }
    } catch (error) {
      notifyError(
        dispatch,
        error?.message ||
          intl.formatMessage({ id: 'connectorViews.list.autoMapFailed' })
      )
    }
  }

  const uploadMappingJson = async (id, e) => {
    e.preventDefault()
    try {
      const formData = new FormData()
      const { name, files } = e.target
      formData.append('mapping_json', files[0], `${name}.json`)
      let res = await postRequest(
        `/api/connector_views/upload-mapping-json/${id}/`,
        formData
      )
      if (res?.status) {
        notifySuccess(
          dispatch,
          intl.formatMessage({ id: 'connectorViews.list.uploadMappingSuccess' })
        )
      } else {
        notifyError(
          dispatch,
          intl.formatMessage({ id: 'connectorViews.list.uploadMappingFailed' })
        )
      }
      getConnectorViewsData()
    } catch (error) {
      notifyError(
        dispatch,
        error?.message ||
          intl.formatMessage({ id: 'connectorViews.list.uploadMappingFailed' })
      )
    }
  }

  const handleDownloadZip = async (id, name) => {
    await downloadAsZip(id, name, dispatch, intl)
  }

  const toggleExpand = useCallback((id) => {
    setExpandedRowKeys((prev) => {
      return prev.includes(id)
        ? prev.filter((key) => key !== id)
        : [...prev, id]
    })
  }, [])

  const expandRow = useCallback((id) => {
    setExpandedRowKeys((prev) => {
      return prev.includes(id) ? prev : [...prev, id]
    })
  }, [])

  useEffect(() => {
    expandedRowKeys.forEach((id) => {
      if (rowJsonMap[id]) return
      getViewJson(id)
    })
  }, [expandedRowKeys, rowJsonMap])

  const getViewJson = async (id) => {
    try {
      let res = await getRequest(`/api/connector_views/json/${id}/`)
      // base.js no longer rejects on a failed request, so the catch below only
      // sees genuine programming errors. Without this branch the error shape
      // itself was rendered in the JSON viewer.
      if (res?.status === false) {
        notifyError(
          dispatch,
          intl.formatMessage({ id: 'connectorViews.list.loadFailed' })
        )
        return
      }
      const { status, ...jsonData } = res
      delete jsonData.svg
      delete jsonData.embedded
      setRowJsonMap((prev) => ({
        ...prev,
        [id]: jsonData,
      }))
    } catch (error) {
      notifyError(
        dispatch,
        error?.message ||
          intl.formatMessage({ id: 'connectorViews.list.loadFailed' })
      )
    }
  }

  const handleRowAction = (option, record) => {
    const action = option?.value
    const fieldName = `row-actions-${record.id}`
    const resetSelect = () => {
      setTimeout(() => form.setFieldValue(fieldName, undefined), 0)
    }

    switch (action) {
      case 'edit_view':
        navigate(`/vehicles/${vehicle}/connector_views/${record.id}/edit`)
        break
      case 'edit_mappings':
        navigate(
          `/vehicles/${vehicle}/connector_views/${record.id}/edit_mappings`
        )
        break
      case 'download_zip':
        handleDownloadZip(record.id, record.name)
        resetSelect()
        break
      case 'upload_mapping_json':
        uploadRowIdRef.current = record.id
        resetSelect()
        fileInputRef.current?.click()
        break
      case 'start_auto_mapping':
        confirmAutoMap(record.id)
        resetSelect()
        break
      case 'download_mapping_logs':
        downloadErrorLogs(
          record.mappingLogs.error,
          'connector_view_mapping_logs.txt'
        )
        resetSelect()
        break
      default:
        resetSelect()
    }
  }

  const rowActionOptions = (record) => {
    const options = [
      {
        value: 'edit_view',
        label: intl.formatMessage({ id: 'connectorViews.list.editView' }),
        icon: <EditOutlined />,
      },
      {
        value: 'edit_mappings',
        label: intl.formatMessage({ id: 'connectorViews.list.editMappings' }),
        icon: <ApartmentOutlined />,
      },
      {
        value: 'download_zip',
        label: intl.formatMessage({ id: 'connectorViews.list.downloadAsZip' }),
        icon: <DownloadOutlined />,
      },
      {
        value: 'upload_mapping_json',
        label: intl.formatMessage({
          id: 'connectorViews.list.uploadMappingJson',
        }),
        icon: <UploadOutlined />,
      },
      {
        value: 'start_auto_mapping',
        label: intl.formatMessage({
          id: 'connectorViews.list.startAutoMapping',
        }),
        icon: <ThunderboltOutlined />,
      },
    ]

    if (record.mappingLogs?.error?.length > 0) {
      options.push({
        value: 'download_mapping_logs',
        label: intl.formatMessage({
          id: 'connectorViews.list.downloadMappingLogs',
        }),
        icon: <FileTextOutlined />,
      })
    }

    return options
  }

  const dataSource = useMemo(
    () =>
      connectorViews.map((cv) => ({
        key: cv.id,
        id: cv.id,
        name: cv.name,
        levels: cv.harness_levels_ids.length,
        harnesses: cv.harness_ids.length,
        created: cv.created_at,
        updated: cv.updated_at,
        compressedPng: cv.compressed_png,
        mappingLogs: cv.mapping_logs,
        raw: cv,
      })),
    [connectorViews]
  )

  const columns = useMemo(
    () => [
      {
        title: intl.formatMessage({ id: 'connectorViews.list.columnName' }),
        dataIndex: 'name',
        sorter: (a, b) => a.name.localeCompare(b.name),
        render: (text, record) => {
          const levelLabel = intl.formatMessage({
            id:
              record.levels === 1
                ? 'connectorViews.list.level'
                : 'connectorViews.list.levels',
          })

          const harnessLabel = intl.formatMessage({
            id:
              record.harnesses === 1
                ? 'connectorViews.list.harness'
                : 'connectorViews.list.harnesses',
          })

          return (
            <div>
              <div className="font-medium" data-testid="vv-row-name">{record.name}</div>
              <div className="text-xs connector-views-name-subtitle">
                {record.levels} {levelLabel} &middot; {record.harnesses}{' '}
                {harnessLabel}
              </div>
            </div>
          )
        },
      },
      {
        title: '',
        className: 'actions-col',
        width: 320,
        render: (_, record) => {
          const isExpanded = expandedRowKeys.includes(record.id)
          const moreMenuItems = [
            {
              key: 'clone_view',
              label: (
                <span data-testid="vv-more-option" data-action="clone_view">
                  {intl.formatMessage({ id: 'connectorViews.list.cloneView' })}
                </span>
              ),
              className: 'dd-menu-item',
            },
            {
              key: 'validate',
              label: (
                <span data-testid="vv-more-option" data-action="validate">
                  {intl.formatMessage({ id: 'connectorViews.list.validateAction' })}
                </span>
              ),
              className: 'dd-menu-item',
            },
            {
              type: 'divider',
            },
            {
              key: 'delete',
              label: (
                <span data-testid="vv-more-option" data-action="delete">
                  {intl.formatMessage({ id: 'connectorViews.list.deleteAction' })}
                </span>
              ),
              icon: (
                <DeleteOutlined
                  style={{ color: 'var(--color-form-link-danger)' }}
                />
              ),
              className: 'dd-menu-item',
              danger: true,
            },
          ]

          return (
            <Space
              size="middle"
              className="connector-view-actions-col"
              style={{ width: '100%' }}
              data-testid="vv-row-actions"
              data-name={record.name}
            >
              <SelectInput
                name={`row-actions-${record.id}`}
                isRequired={false}
                hideLabel
                showSearch={false}
                allowClear={false}
                placeholder={intl.formatMessage({
                  id: 'connectorViews.list.actions',
                })}
                options={rowActionOptions(record)}
                formatOption={(option) => (
                  <Flex align="center" gap={8} data-testid="vv-row-action" data-action={option.value}>
                    {option.icon}
                    {option.label}
                  </Flex>
                )}
                onMenuOpen={() => expandRow(record.id)}
                onChange={(option) => handleRowAction(option, record)}
                selectClass="row-action-select"
                selectPopupClass="wrap-options"
              />

              <Button
                type="link"
                className={`${isExpanded ? 'link-button neutral' : 'link-button'}`}
                onClick={() => toggleExpand(record.id)}
                testId="vv-detail"
              >
                {isExpanded
                  ? intl.formatMessage({ id: 'connectorViews.list.hide' })
                  : intl.formatMessage({ id: 'connectorViews.list.detail' })}
              </Button>

              <Dropdown
                trigger={['click']}
                placement="bottomRight"
                overlayStyle={{ marginTop: 12 }}
                menu={{
                  items: moreMenuItems,
                  onClick: ({ key }) => {
                    if (key === 'clone_view')
                      navigate(
                        `/vehicles/${vehicle}/connector_views/new?clone_from=${record.id}`
                      )
                    if (key === 'validate') confirmValidate(record.id)
                    if (key === 'delete') confirmDelete(record.id, record.name)
                  },
                }}
              >
                <Button
                  icon={<MoreOutlined />}
                  onClick={() => expandRow(record.id)}
                  testId="vv-more"
                />
              </Dropdown>
            </Space>
          )
        },
      },
    ],
    [vehicle, expandedRowKeys, navigate, toggleExpand, expandRow, form, intl]
  )

  const expandedRowRender = useCallback(
    (record) => {
      const jsonData = rowJsonMap[record.id]
      return (
        <div className="connector-view-expanded" data-testid="vv-expanded">
          <div className="expanded-image">
            <Image
              preview={{
                rootClassName: 'connector-image-preview',
              }}
              src={record.compressedPng}
              alt={record.name}
              height={160}
              style={{
                maxWidth: '100%',
                objectFit: 'contain',
                objectPosition: 'left top',
              }}
            />
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              {intl.formatMessage({ id: 'connectorViews.form.clickToViewFullSize' })}
            </Typography.Text>
            <div className="expanded-meta">
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                {intl.formatMessage({ id: 'connectorViews.list.columnCreated' })}:{' '}
                {new Date(record.created).toLocaleString()}
              </Typography.Text>
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                {intl.formatMessage({ id: 'connectorViews.list.columnUpdated' })}:{' '}
                {new Date(record.updated).toLocaleString()}
              </Typography.Text>
            </div>
          </div>
          <div className="expanded-content">
            {jsonData ? (
              <JsonTreeSearch jsonData={jsonData} />
            ) : (
              <Typography.Text type="secondary">
                {intl.formatMessage({ id: 'connectorViews.list.jsonLoading' })}
              </Typography.Text>
            )}
          </div>
        </div>
      )
    },
    [rowJsonMap, intl]
  )

  const { currentPage, totalCount, totalPages, nextPageLink, prevPageLink } =
    pagination

  return (
    <div className="page connector-views">
      <Row className="body">
        <Form form={form} className="connector-views-form">
          <Space direction="horizontal" size="middle" className="data-filters">
            <InputText
              type="search"
              name="search"
              placeholder={intl.formatMessage({
                id: 'connectorViews.list.searchPlaceholder',
              })}
              initialValue={searchQuery}
              isRequired={false}
              onChange={(value) => {
                setSearchQuery(value)
              }}
              wrapperClass="search-wrapper"
            />
            <InputText
              type="search"
              name="harness_search"
              placeholder={intl.formatMessage({
                id: 'connectorViews.list.searchHarnessPlaceholder',
              })}
              initialValue={harnessSearchQuery}
              isRequired={false}
              onChange={(value) => {
                setHarnessSearchQuery(value)
              }}
              wrapperClass="search-wrapper"
            />
            <LinkWithStatus
              href={`/vehicles/${vehicle}/connector_views/new`}
              type="primary"
              testId="vv-new"
              title={intl.formatMessage({
                id: 'connectorViews.form.newVehicleView',
              })}
            />
          </Space>

          <Table
            rowKey="id"
            columns={columns}
            dataSource={dataSource}
            showSorterTooltip={false}
            // Fixed layout keeps column widths stable regardless of expanded-row
            // content (tree/image) - auto layout would re-distribute columns.
            tableLayout="fixed"
            loading={loading}
            expandable={{
              expandIcon: () => null,
              showExpandColumn: false,
              expandedRowKeys,
              expandedRowRender,
              rowExpandable: () => true,
            }}
            pagination={
              totalPages > 1
                ? {
                    current: currentPage,
                    total: totalCount,
                    pageSize: PAGE_SIZE,
                    showSizeChanger: false,
                    hideOnSinglePage: true,
                    showTotal: (total, range) => <RecordsCount count={total} />,
                    onChange: (page) => {
                      const offset = (page - 1) * PAGE_SIZE
                      const url = nextPageLink
                        ? new URL(nextPageLink)
                        : new URL(prevPageLink)
                      url.searchParams.set('offset', offset)
                      getConnectorViewsData(url.toString())
                    },
                  }
                : false
            }
            locale={{
              emptyText: (
                <div style={{ padding: '40px' }}>
                  <Empty description={<RecordsCount count={0} />} />
                </div>
              ),
            }}
          />
        </Form>
        {pagination && totalPages <= 1 && totalCount > 0 && (
          <Typography.Text>
            <RecordsCount count={totalCount} />
          </Typography.Text>
        )}
      </Row>
      <input
        ref={fileInputRef}
        type="file"
        accept="application/json"
        style={{ display: 'none' }}
        onChange={(e) => {
          uploadMappingJson(uploadRowIdRef.current, e)
          e.target.value = ''
        }}
      />
      {contextHolder}
    </div>
  )
}

export default ConnectorViewsTable
